// Локальный веб-профиль (barter-024): стабильный id + редактируемое имя для
// пользователей без Telegram. Пару id+secret выдаёт воркер (POST /web-session)
// при первой регистрации; хранится в Dexie и шлётся с запросами (services/auth).
// Веб-id из зарезервированного диапазона (см. packages/worker web-session.ts),
// поэтому лимиты/баны/владение объявлениями работают как у ТГ-пользователей.

import { db } from '../db/database';
import { API_BASE } from './api-base';

export interface WebProfile {
  id: number;
  secret: string;
  name: string;
}

const KEY = 'local';

let cache: WebProfile | null = null;

async function readStored(): Promise<WebProfile | null> {
  const rec = await db.webProfile.get(KEY);
  if (!rec || typeof rec.id !== 'number' || typeof rec.secret !== 'string') return null;
  return { id: rec.id, secret: rec.secret, name: rec.name || '' };
}

async function writeStored(profile: WebProfile): Promise<void> {
  await db.webProfile.put({ key: KEY, ...profile, createdAt: Date.now() });
}

interface SessionResponse {
  ok: boolean;
  id: number;
  secret?: string;
  created: boolean;
}

async function postSession(body: Record<string, unknown>): Promise<SessionResponse> {
  const res = await fetch(`${API_BASE}/web-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as SessionResponse;
}

/**
 * Гарантирует профиль: сохранённая пара валидируется на сервере (устаревшая —
 * тихо перерегистрируется), нового профиля нет — регистрируется. Без сети
 * сохранённый профиль остаётся как есть (офлайн-первичность: очередь операций
 * довезёт данные позже); если профиля нет и сети нет — ошибка.
 */
export async function ensureWebProfile(): Promise<WebProfile> {
  if (cache) return cache;
  const stored = await readStored();
  if (!stored) {
    const res = await postSession({});
    const profile: WebProfile = { id: res.id, secret: res.secret!, name: '' };
    await writeStored(profile);
    cache = profile;
    return profile;
  }
  try {
    const res = await postSession({ id: stored.id, secret: stored.secret, name: stored.name });
    if (res.created && res.secret) {
      // старая пара перестала действовать (например, D1 чистили) — живём с новой
      const fresh: WebProfile = { id: res.id, secret: res.secret, name: stored.name };
      await writeStored(fresh);
      cache = fresh;
      return fresh;
    }
    cache = stored;
    return stored;
  } catch {
    cache = stored; // офлайн — работаем на сохранённой паре
    return stored;
  }
}

/** Сохранённая пара для заголовков без сети; null — профиля ещё нет. */
export async function getWebProfileAuth(): Promise<WebProfile | null> {
  if (cache) return cache;
  return readStored();
}

/**
 * Смена имени: Dexie сразу, сервер — best-effort (довезёт при следующей
 * валидации сессии, ensureWebProfile шлёт сохранённое имя).
 */
export async function renameWebProfile(name: string): Promise<void> {
  const stored = cache ?? (await readStored());
  if (!stored) return;
  const updated = { ...stored, name };
  cache = updated;
  await writeStored(updated);
  postSession({ id: stored.id, secret: stored.secret, name }).catch(() => {});
}

/** Только для тестов: сбросить кэш модуля. */
export function resetWebProfileCache(): void {
  cache = null;
}
